import type { FileType } from './fileUpload/fileType'
import type { SelectType } from '@repo/ui'
import type { SelectOption } from '@repo/ui'
import type { CreateQueryResult } from '@tanstack/svelte-query'
import type { FullAutoFill } from 'svelte/elements'

export enum InputType {
	text,
	textarea,
	url,
	number,
	password,
	email,
	select,
	multiselect,
	image,
	code,
	file,
	tel,
	radioGroup,
}

export enum FormItemType {
	group,
	tabs,
}

export type FormType = (FormInputType | FormGroupType)[]

export interface FormInputType<T = string> {
	name: string
	value?: string
	values?: string[]
	files?: FileList
	type?: InputType
	options?: SelectOption<T>[]
	label?: string
	placeholder?: string
	description?: string
	warning?: string
	required?: boolean
	minlength?: number
	maxlength?: number
	accept?: FileType
	showSelectAll?: boolean
	showAllPlaceholder?: boolean
	selectType?: SelectType
	param?: string
	query?: () => CreateQueryResult<object>
	mapOptionsFn?: (data: any) => SelectOption<T>[]
	autocomplete?: FullAutoFill
}

export interface FormGroupType {
	formItemType: FormItemType
	items: FormInputType[]
}

export function Form(...formItems: FormType) {
	return formItems
}

export function FormGroup(...formInputs: FormInputType[]): FormGroupType {
	return {
		formItemType: FormItemType.group,
		items: formInputs,
	}
}

export function FormTabs(...formInputs: FormInputType[]): FormGroupType {
	return {
		formItemType: FormItemType.tabs,
		items: formInputs,
	}
}

export function FormInput<T = string>(params: FormInputType<T>): FormInputType<T> {
	return {
		name: params.name,
		label: params.label,
		value: params.value || null,
		values: params.values || params.type === InputType.multiselect ? [] : null,
		options: params.options || null,
		type: params.type || InputType.text,
		placeholder: params.placeholder || null,
		minlength: params.minlength || null,
		maxlength: params.maxlength || null,
		required: params.required,
		showSelectAll: params.showSelectAll || false,
		showAllPlaceholder: params.showAllPlaceholder || false,
		selectType: params.selectType || null,
		accept: params.accept || null,
		param: params.param || null,
		query: params.query || null,
		mapOptionsFn: params.mapOptionsFn || null,
		files: params.files || null,
		autocomplete: params.autocomplete || null,
	}
}
